import {
    IonBadge,
    IonButton,
    IonButtons,
    IonCard,
    IonCardContent,
    IonCardHeader,
    IonChip,
    IonCardSubtitle,
    IonCardTitle,
    IonCheckbox,
    IonContent,
    IonHeader,
    IonIcon,
    IonItem,
    IonLabel,
    IonList,
    IonMenuButton,
    IonPage,
    IonSearchbar,
    IonText,
    IonTitle,
    IonToolbar
} from "@ionic/react";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import "./AdminOverview.css"
import createAxiosInstance from "../../services/AxiosInstance";
import Controller from "../iot/Controller";
import {notificationsOutline, settingsOutline} from "ionicons/icons";
import ProfilePicture from "../../components/shared/ProfilePicture";
import CurrencyDisplay from "../../components/transactions/CurrencyDisplay";
import {User} from "../../model/User";
import {MENU_MEMBER_ROLES, ROLES} from "../../auth/rbac";
import {getPushRegistrationSummary, PushRegistrationSummary} from "../../utils/pushRegistrationSummary";

const ROLE_LABELS: Record<string, string> = {
    [ROLES.USER]: 'Leden',
    [ROLES.REGULAR_GUEST]: 'Vaste gast',
    [ROLES.DERTIGEN_USER]: 'Dertigen gebruiker',
    [ROLES.ADMIN]: 'Admin',
    [ROLES.RULE_BOSS]: 'Regelbaas',
    [ROLES.GUEST]: 'Gast',
};

const getRoleLabel = (role: string): string => ROLE_LABELS[role] ?? role;

const PLATFORM_LABELS: Record<string, string> = {
    web: 'Web',
    ios: 'iOS',
    android: 'Android',
};

const shortDateFormatter = new Intl.DateTimeFormat('nl-NL', {
    day: 'numeric',
    month: 'short',
});

const getPlatformLabel = (platform: string): string => PLATFORM_LABELS[platform] ?? platform;

const formatPushSeenAt = (value: string | null): string | null => {
    if (!value) {
        return null;
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return null;
    }

    return shortDateFormatter.format(parsed);
};

const getPushStatusLabel = (summary: PushRegistrationSummary): string => {
    if (summary.totalRegistrations === 0) {
        return 'Niet gekoppeld';
    }

    if (summary.enabledRegistrations > 0) {
        return 'Push actief';
    }

    return 'Uitgeschakeld';
};

const getPushStatusColor = (summary: PushRegistrationSummary): 'success' | 'warning' | 'medium' => {
    if (summary.enabledRegistrations > 0) {
        return 'success';
    }

    return 'medium';
};

const getPushStatusDetail = (summary: PushRegistrationSummary): string => {
    if (summary.totalRegistrations === 0) {
        return 'Nog geen gekoppelde apparaten voor pushmeldingen.';
    }

    const parts: string[] = [];
    parts.push(summary.enabledRegistrations > 0
        ? `${summary.enabledRegistrations} actief van ${summary.totalRegistrations}`
        : `${summary.totalRegistrations} apparaat${summary.totalRegistrations > 1 ? 'en' : ''} bekend`);

    if (summary.platforms.length > 0) {
        parts.push(summary.platforms.map(getPlatformLabel).join(', '));
    }

    const lastSeenLabel = formatPushSeenAt(summary.latestSeenAt);
    if (lastSeenLabel) {
        parts.push(`laatst gezien ${lastSeenLabel}`);
    }

    return parts.join(' • ');
};

const AdminOverview: React.FC = () => {

    const [users, setUsers] = useState<User[]>([])
    const [selectedRoles, setSelectedRoles] = useState<string[]>([...MENU_MEMBER_ROLES]);
    const axios = useMemo(() => createAxiosInstance(), []);
    const [isFilterVisible, setIsFilterVisible] = useState<boolean>(false);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [totalBalance, setTotalBalance] = useState<number>();

    const roles = [...MENU_MEMBER_ROLES, ROLES.DERTIGEN_USER];

    const fetchUsers = useCallback(() => {
        axios.get(`/user`)
            .then(res => {
                setUsers(Array.isArray(res.data.players) ? res.data.players : [])
                setTotalBalance(Number(res.data.totalBalance))
            })
    }, [axios])

    const handleRoleChange = (role: string) => {
        const updatedRoles = selectedRoles.includes(role)
            ? selectedRoles.filter(r => r !== role)
            : [...selectedRoles, role];
        setSelectedRoles(updatedRoles);
    }

    const toggleFilterVisibility = () => {
        setIsFilterVisible(!isFilterVisible);
    }

    const getRoleClass = (roles: string[] | undefined) => {
        if (roles === undefined) return ''
        return roles.length > 0 ? `role-${roles[0]}` : '';
    };

    const visibleUsers = useMemo(() => {
        const normalizedSearch = searchTerm.trim().toLowerCase();

        return users
            .filter((user: User) => user.roles?.some((role: string) => selectedRoles.includes(role)))
            .filter((user: User) => {
                if (!normalizedSearch) {
                    return true;
                }

                const haystack = [user.name, user.emailAddress, ...(user.roles ?? [])]
                    .filter(Boolean)
                    .join(' ')
                    .toLowerCase();

                return haystack.includes(normalizedSearch);
            })
            .sort((left, right) => left.name.localeCompare(right.name));
    }, [searchTerm, selectedRoles, users]);

    const visiblePushMetrics = useMemo(() => {
        return visibleUsers.reduce((accumulator, user) => {
            const summary = getPushRegistrationSummary(user);

            if (summary.enabledRegistrations > 0) {
                accumulator.active += 1;
            }

            return accumulator;
        }, {active: 0, pending: 0});
    }, [visibleUsers]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers])


    return (
        <IonPage>
            <IonHeader>
                <IonToolbar>
                    <IonTitle>Admin overzicht</IonTitle>
                    <IonButtons slot="start">
                        <IonMenuButton/>
                    </IonButtons>
                    <IonButton slot="end" onClick={toggleFilterVisibility}>
                        <IonIcon icon={settingsOutline} slot="icon-only"/>
                    </IonButton>
                </IonToolbar>
            </IonHeader>

            <IonContent fullscreen>
                <div className="admin-overview-content">
                    <div className="admin-overview-top-grid">
                        <IonCard className="admin-summary-card">
                            <IonCardHeader>
                                <IonCardSubtitle>Admin overzicht</IonCardSubtitle>
                                <IonCardTitle>{visibleUsers.length} zichtbare accounts</IonCardTitle>
                            </IonCardHeader>
                            <IonCardContent>
                                <IonSearchbar
                                    value={searchTerm}
                                    onIonInput={(event) => setSearchTerm(event.detail.value ?? '')}
                                    placeholder="Zoek op naam, mail of rol"
                                    className="admin-searchbar"
                                />
                                <div className="admin-summary-metrics">
                                    <div className="admin-metric-tile">
                                        <IonText color="medium">In beeld</IonText>
                                        <strong>{visibleUsers.length}</strong>
                                    </div>
                                    <div className="admin-metric-tile">
                                        <IonText color="medium">Push actief</IonText>
                                        <strong>{visiblePushMetrics.active}</strong>
                                    </div>
                                    <div className="admin-metric-tile">
                                        <IonText color="medium">Push volgt</IonText>
                                        <strong>{visiblePushMetrics.pending}</strong>
                                    </div>
                                </div>
                                <div className="admin-active-filters">
                                    {selectedRoles.map((role) => (
                                        <IonChip key={role} outline>
                                            <IonLabel>{getRoleLabel(role)}</IonLabel>
                                        </IonChip>
                                    ))}
                                </div>
                            </IonCardContent>
                        </IonCard>

                        <IonCard className="admin-summary-card total-balance-card">
                            <IonCardHeader>
                                <IonCardSubtitle>Keet balans</IonCardSubtitle>
                                <IonCardTitle>Open saldo</IonCardTitle>
                            </IonCardHeader>
                            <IonCardContent>
                                <CurrencyDisplay amount={totalBalance ?? 0}/>
                            </IonCardContent>
                        </IonCard>
                    </div>

                    {isFilterVisible && (
                        <IonCard className="admin-filter-card">
                            <IonCardHeader>
                                <IonCardSubtitle>Filter</IonCardSubtitle>
                                <IonCardTitle>Toon alleen relevante rollen</IonCardTitle>
                            </IonCardHeader>
                            <IonCardContent>
                                <div className={'role-container'}>
                                    {roles.map(role => (
                                        <IonItem key={role} lines="none" className="role-filter-item">
                                            <IonLabel>{getRoleLabel(role)}</IonLabel>
                                            <IonCheckbox
                                                checked={selectedRoles.includes(role)}
                                                onIonChange={() => handleRoleChange(role)}
                                            />
                                        </IonItem>
                                    ))}
                                </div>
                            </IonCardContent>
                        </IonCard>
                    )}

                    <div className="push-broadcast-container">
                        <IonCard button routerLink="/admin/nfc">
                            <IonCardHeader>
                                <IonCardTitle>NFC-chip schrijven</IonCardTitle>
                                <IonCardSubtitle>Stel een alarmchip in of schrijf en controleer eigen tekst</IonCardSubtitle>
                            </IonCardHeader>
                            <IonCardContent>Open de NFC-schrijver</IonCardContent>
                        </IonCard>
                        <IonCard button routerLink="/admin/push-notifications">
                            <IonCardHeader>
                                <IonCardTitle>Push broadcast</IonCardTitle>
                                <IonCardSubtitle>Open de aparte pagina om een melding naar alle geregistreerde apparaten te sturen</IonCardSubtitle>
                            </IonCardHeader>
                            <IonCardContent>
                                <div className="push-broadcast-link-row">
                                    <IonIcon icon={notificationsOutline} className="push-broadcast-icon"/>
                                    <IonLabel>Ga naar de push broadcast pagina</IonLabel>
                                </div>
                            </IonCardContent>
                        </IonCard>
                    </div>

                    <IonList className="admin-list" lines="none">
                        {visibleUsers.map((value: User) => {
                            const pushSummary = getPushRegistrationSummary(value);

                            return (
                                <IonItem
                                    routerLink={`/admin/${value.id}`}
                                    className={`admin-list-item ${getRoleClass(value.roles)}`}
                                    key={value.id ?? value.name}
                                >
                                    <div className="admin-list-item-content">
                                        <div className="admin-user-summary">
                                            <ProfilePicture
                                                profilePicUrl={(value.profilePictureUrl?.length !== 0 && value.profilePictureUrl) ? `${process.env.REACT_APP_STORAGE}${value.profilePictureUrl}` : `${process.env.REACT_APP_STORAGE}default-profile.jpg`}
                                                showEditable={false}
                                                width={46}
                                                height={46}
                                                fullName={value.name}
                                            />
                                            <div className="admin-user-copy">
                                                <IonLabel className="admin-user-name">{value.name}</IonLabel>
                                                <IonText color="medium" className="admin-user-email">
                                                    {value.emailAddress || 'Geen e-mailadres'}
                                                </IonText>
                                            </div>
                                        </div>

                                        <div className="admin-user-status-row">
                                            <IonBadge color={getPushStatusColor(pushSummary)} className="admin-status-badge">
                                                {getPushStatusLabel(pushSummary)}
                                            </IonBadge>
                                            <IonText color="light" className="admin-push-summary">
                                                {getPushStatusDetail(pushSummary)}
                                            </IonText>
                                        </div>

                                        <div className="admin-user-meta">
                                            {(value.roles ?? []).map((role) => (
                                                <IonBadge key={role} color="light" className="admin-role-badge">
                                                    {getRoleLabel(role)}
                                                </IonBadge>
                                            ))}
                                        </div>
                                    </div>
                                </IonItem>
                            );
                        })}
                    </IonList>

                    {visibleUsers.length === 0 && (
                        <IonCard className="admin-empty-state">
                            <IonCardHeader>
                                <IonCardTitle>Geen accounts gevonden</IonCardTitle>
                                <IonCardSubtitle>Pas je zoekterm of rolfilters aan om weer resultaten te zien.</IonCardSubtitle>
                            </IonCardHeader>
                        </IonCard>
                    )}
                </div>
            </IonContent>
        </IonPage>
    );
}

export default AdminOverview;
